import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import ButtonBase from '@mui/material/ButtonBase'
import get from 'lodash/get'
import map from 'lodash/map'
import filter from 'lodash/filter'
import flatMap from 'lodash/flatMap'
import compact from 'lodash/compact'
import Flex from '../../../../shared/components/layouts/Flex'
import FlexColumn from '../../../../shared/components/layouts/FlexColumn'
import { useNodes } from '../../../../contexts/NodesContext'
import { useProjects } from '../../../../contexts/ProjectsContext'
import { useServices } from '../../../../contexts/ServicesContext'
import { getNodeStatus, getLoadLevel, getLoadColor } from '../utils/nodeStatus'

const isServiceRunning = (service) => {
  const statuses = get(service, 'currentStatuses', [])
  return statuses.length > 0 && statuses[0].state === 'running'
}

function NodeCard({ node, onClick, disabled }) {
  const status = getNodeStatus(node)
  const latestStatus = get(node, 'latestStatus', {})
  const loadLevel = getLoadLevel(latestStatus)
  const isOnline = status === 'online'

  return (
    <ButtonBase
      onClick={() => onClick(node.id, null)}
      disabled={disabled}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Flex alignItems="center" gap={1.5}>
        <Box
          sx={{
            width: 12,
            height: 12,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: isOnline ? getLoadColor(loadLevel) : 'grey.500',
          }}
        />
        <FlexColumn gap={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {node.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }} noWrap>
            {node.ip || '-'}
          </Typography>
        </FlexColumn>
      </Flex>
    </ButtonBase>
  )
}

function ServiceCard({ service, projectName, nodeName, onClick, disabled }) {
  const running = isServiceRunning(service)
  const isDisabled = disabled || !running

  return (
    <ButtonBase
      onClick={() => onClick(service._nodeId, service.id)}
      disabled={isDisabled}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        opacity: running ? 1 : 0.5,
        '&:hover': {
          bgcolor: running ? 'action.hover' : undefined,
        },
      }}
    >
      <Flex alignItems="center" gap={1.5}>
        <Box
          sx={{
            width: 12,
            height: 12,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: running ? 'success.main' : 'error.main',
          }}
        />
        <FlexColumn gap={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {service.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {projectName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {nodeName}
          </Typography>
        </FlexColumn>
      </Flex>
    </ButtonBase>
  )
}

function ConnectionPicker({ onConnect, disabled }) {
  const { nodes, nodesById, loading: nodesLoading } = useNodes()
  const { services, loading: servicesLoading } = useServices()
  const { projectsById, loading: projectsLoading } = useProjects()

  const isLoading = nodesLoading || servicesLoading || projectsLoading

  const enrichedServices = useMemo(() => {
    return compact(flatMap(services, (service) => {
      const project = get(projectsById, service.dockerProjectId)
      if (!project) return null
      const nodeIds = get(project, 'nodeIds', [])
      return map(nodeIds, (nodeId) => {
        const node = get(nodesById, nodeId)
        return {
          ...service,
          _nodeId: nodeId,
          _projectName: get(project, 'name', ''),
          _nodeName: get(node, 'name', ''),
        }
      })
    }))
  }, [services, projectsById, nodesById])

  const runningServices = useMemo(() => {
    return filter(enrichedServices, isServiceRunning)
  }, [enrichedServices])

  const stoppedServices = useMemo(() => {
    return filter(enrichedServices, (s) => !isServiceRunning(s))
  }, [enrichedServices])

  const sortedServices = useMemo(() => {
    return [...runningServices, ...stoppedServices]
  }, [runningServices, stoppedServices])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
      {/* Group 1: Nodes */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 2 }}>
        <FormattedMessage id="terminal.connectionPicker.nodes" />
      </Typography>
      {nodes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          <FormattedMessage id="terminal.connectionPicker.noNodes" />
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
            mb: 4,
          }}
        >
          {map(nodes, (node) => (
            <NodeCard
              key={node.id}
              node={node}
              onClick={onConnect}
              disabled={disabled}
            />
          ))}
        </Box>
      )}

      {/* Group 2: Services */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 2 }}>
        <FormattedMessage id="terminal.connectionPicker.services" />
      </Typography>
      {sortedServices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage id="terminal.connectionPicker.noServices" />
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          {map(sortedServices, (service) => (
            <ServiceCard
              key={`${service.id}-${service._nodeId}`}
              service={service}
              projectName={service._projectName}
              nodeName={service._nodeName}
              onClick={onConnect}
              disabled={disabled}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default ConnectionPicker
